import { FileUpload } from '../models/file-upload';
import { transferTray } from './transfer-tray.service';

/**
 * Take the file the user just produced to the next tool, without them touching their disk.
 *
 * The result screen could only ever *link* to the next tool. The output stayed behind, so
 * "compress this GIF I just made" meant downloading their own result and uploading it again —
 * two manual steps and a trip through the file manager, on the exact screen where they had just
 * succeeded. T3.1 measured this as 3.5% of continuations (the rest are same-tool re-runs, which
 * Re-edit covers), but it is the 3.5% where the product visibly gives up.
 *
 * **Why it fetches the output rather than using `/uploads/from-output`.** That endpoint exists
 * and is the efficient answer — it hardlinks the output into the upload directory server-side and
 * hands back a registered input, costing zero bytes. What it cannot do yet is *stage* the result:
 * the destination stages through `fileSelected`, which requires real `File` instances
 * (`f instanceof File`), so a server handle needs a staging path that does not exist. Building
 * that means new branches in `FileUploadManager` — the upload path, where a regression is most
 * expensive.
 *
 * So this carries the bytes through the tray instead, and the destination stages them through
 * exactly the same path a drop takes. Against the status quo it is not a bandwidth regression:
 * the user was going to download and re-upload the same file by hand. It is one download they no
 * longer perform themselves, and zero new code in the upload path.
 *
 * See docs/proposed/CHAINING_DESIGN.md and RETENTION_FIXES.md T3.4.
 */

/** Anything larger is not worth pulling through the browser to save a file-picker trip. */
export const CHAIN_MAX_BYTES = 100 * 1024 * 1024;

export type ChainFailure = 'too-large' | 'unavailable' | 'tray-unavailable';

export interface ChainResult {
    /** Destination URL to navigate to — carries the file when `carried` is true. */
    href: string;
    carried: boolean;
    reason?: ChainFailure;
}

/**
 * Build the destination URL for a continuation, carrying the output when possible.
 *
 * Never throws and never blocks navigation: every failure returns a plain link to the
 * destination, which is exactly the behaviour before this existed. The caller reports the
 * difference to the user rather than silently landing them on an empty uploader.
 */
export async function chainToTool(
    fileUpload: FileUpload,
    slug: string,
    downloadUrl: string | null,
): Promise<ChainResult> {
    const href = `/${slug.replace(/^\//, '')}`;
    try {
        if (!downloadUrl) return { href, carried: false, reason: 'unavailable' };

        const size = Number(fileUpload?.result?.outputSize ?? 0);
        if (size > CHAIN_MAX_BYTES) return { href, carried: false, reason: 'too-large' };

        const response = await fetch(downloadUrl);
        if (!response.ok) return { href, carried: false, reason: 'unavailable' };
        const blob = await response.blob();
        // Guard again on the real size: outputSize is metadata and can be absent or stale.
        if (blob.size > CHAIN_MAX_BYTES) return { href, carried: false, reason: 'too-large' };

        const file = new File([blob], readableOutputName(fileUpload), {
            type: blob.type || 'application/octet-stream',
        });
        const xferId = await transferTray.put([file], fileUpload.uniqueProcessType || '', fileUpload.name || '');
        if (!xferId) return { href, carried: false, reason: 'tray-unavailable' };

        return { href: `${href}?xfer=${encodeURIComponent(xferId)}`, carried: true };
    } catch {
        // Offline, blocked by the network, aborted — all the same to the user: they still get
        // the tool, just without the file already in it.
        return { href, carried: false, reason: 'unavailable' };
    }
}

/**
 * A name the user recognises for the output.
 *
 * `fileUpload.name` is the input's name; the output shares it but not its extension, so
 * "holiday.gif" converted to MP4 must arrive as "holiday.mp4" or the destination rejects it on
 * extension before the user sees anything.
 */
export function readableOutputName(fileUpload: FileUpload): string {
    const base = (fileUpload?.name || 'file').replace(/\.[^.]+$/, '');
    const ext = `${fileUpload?.toType || ''}`.toLowerCase().replace(/[^a-z0-9]/g, '');
    return ext ? `${base}.${ext}` : base;
}
